// A number answered with − and + (SPEC-23, Sono's hours): the lib's item
// list gives the bounds and the step, the flow starts it at the item's
// initial value. At either bound its button is off, so a tap does nothing.
// With no initial value (LEAF-Q's « Quantas? ») it shows no number until
// the first + gives the lowest.

import type { Question } from "poweryourflow-lib";

import { copy } from "../i18n/pt-PT";

const text = copy.questionnaire;

const roundButton =
  "flex size-[var(--touch-min)] shrink-0 items-center justify-center rounded-pill border border-line-15 bg-white text-title text-dark " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf hover:bg-cream-deep disabled:text-disabled disabled:hover:bg-white";

/** "7,5": a decimal comma, as pt-PT writes it. */
const decimal = (value: number) => value.toLocaleString("pt-PT");

export function Stepper({
  question,
  value,
  unit,
  label,
  onChange,
}: {
  question: Question;
  value: number | undefined;
  unit: string | undefined;
  /** A field's own title, drawn in place of the hint under an option. */
  label?: string;
  onChange: (value: number) => void;
}) {
  const min = question.min ?? 0;
  const max = question.max ?? 0;
  const step = question.step ?? 1;
  const shown = value ?? min;
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className={label ? "pb-2 text-body-sm font-semibold text-dark" : "pb-3 text-body-sm text-muted"}>
        {label ?? text.hints.number}
      </legend>
      <div className="flex items-center justify-between gap-4 rounded-card-inner bg-white px-5 py-4 shadow-option">
        <button
          type="button"
          disabled={value === undefined || shown <= min}
          onClick={() => onChange(Math.max(min, shown - step))}
          className={roundButton}
        >
          <span aria-hidden="true">−</span>
          <span className="sr-only">{text.less}</span>
        </button>
        <output aria-live="polite" className="text-numeral leading-none font-medium text-dark">
          {value === undefined ? text.noNumber : decimal(value)}
          {unit && <span className="pl-1 text-title font-medium text-muted">{unit}</span>}
        </output>
        <button
          type="button"
          disabled={shown >= max}
          onClick={() => onChange(value === undefined ? min : Math.min(max, shown + step))}
          className={roundButton}
        >
          <span aria-hidden="true">+</span>
          <span className="sr-only">{text.more}</span>
        </button>
      </div>
    </fieldset>
  );
}
